import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  MeetingMinutes,
  MeetingMinutesAttachment,
  MeetingMinutesPayload,
} from '../types/meetingMinutes';

export async function fetchMeetingMinutesList(
  keyword?: string,
  contractId?: number | null
): Promise<MeetingMinutes[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<MeetingMinutes[]>('/meeting-minutes', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchMeetingMinutes(meetingMinutesId: number): Promise<MeetingMinutes> {
  const { data } = await apiClient.get<MeetingMinutes>(`/meeting-minutes/${meetingMinutesId}`);
  return data;
}

export async function createMeetingMinutes(payload: MeetingMinutesPayload): Promise<MeetingMinutes> {
  const { data } = await apiClient.post<MeetingMinutes>('/meeting-minutes', payload);
  return data;
}

export async function updateMeetingMinutes(
  meetingMinutesId: number,
  payload: MeetingMinutesPayload
): Promise<MeetingMinutes> {
  const { data } = await apiClient.patch<MeetingMinutes>(
    `/meeting-minutes/${meetingMinutesId}`,
    payload
  );
  return data;
}

export async function deleteMeetingMinutes(meetingMinutesId: number): Promise<void> {
  await apiClient.delete(`/meeting-minutes/${meetingMinutesId}`);
}

export async function approveMeetingMinutes(meetingMinutesId: number): Promise<MeetingMinutes> {
  const { data } = await apiClient.post<MeetingMinutes>(`/meeting-minutes/${meetingMinutesId}/approve`);
  return data;
}

export async function revokeMeetingMinutes(meetingMinutesId: number): Promise<MeetingMinutes> {
  const { data } = await apiClient.post<MeetingMinutes>(`/meeting-minutes/${meetingMinutesId}/revoke`);
  return data;
}

export async function uploadMeetingMinutesAttachments(
  meetingMinutesId: number,
  files: File[]
): Promise<MeetingMinutesAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<MeetingMinutesAttachment[]>(
    `/meeting-minutes/${meetingMinutesId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteMeetingMinutesAttachment(
  meetingMinutesId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(`/meeting-minutes/${meetingMinutesId}/attachments/${attachmentId}`);
}

export async function downloadMeetingMinutesAttachment(
  meetingMinutesId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/meeting-minutes/${meetingMinutesId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function downloadMeetingMinutesPdf(
  meetingMinutesId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/meeting-minutes/${meetingMinutesId}/pdf`,
    fileName || `회의록_${meetingMinutesId}.pdf`
  );
}
